import React, { useState } from "react";
import { Plus, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { finApi, finErr } from "@/lib/financeApi";
import { btnGold, Chip, Empty, ErrorBox, field, Loading, useLoad, VENDOR_TYPE_CHIP } from "@/components/nds/windows/finance/parts";

export const VENDOR_FIELDS = [
  ["name", "Name"], ["contact_name", "Contact"], ["email", "Email"], ["phone", "Phone"], ["address", "Address"], ["tax_id", "Tax ID"], ["bill_rate", "Bill rate $/hr"], ["pay_rate", "Pay rate $/hr"], ["bank_last4", "Bank last 4"],
];
export const blankVendor = () => ({ name: "", type: "landlord", contact_name: "", email: "", phone: "", address: "", payment_method: "check", bank_last4: "", notes: "", active: true });

export function VendorFields({ v, set, prefix }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
      {VENDOR_FIELDS.map(([k, label]) => (
        <label key={k} className="space-y-1 block"><span className="nds-label-dim text-[9px]">{label}</span>
          <input required={k === "name"} maxLength={k === "bank_last4" ? 4 : undefined} value={v[k] || ""} onChange={set(k)} data-testid={`${prefix}-${k}`} className={`${field} w-full`} /></label>
      ))}
      <label className="space-y-1 block"><span className="nds-label-dim text-[9px]">Type</span>
        <select value={v.type} onChange={set("type")} data-testid={`${prefix}-type`} className={`${field} w-full`}>
          {Object.entries(VENDOR_TYPE_CHIP).map(([k, [, l]]) => <option key={k} value={k}>{l}</option>)}
        </select></label>
      <label className="space-y-1 block"><span className="nds-label-dim text-[9px]">Payment method</span>
        <select value={v.payment_method} onChange={set("payment_method")} data-testid={`${prefix}-payment_method`} className={`${field} w-full`}>
          <option value="check">Check</option><option value="ach">ACH</option><option value="wire">Wire</option>
        </select></label>
      <label className="flex items-center gap-2 pt-5 text-sm text-nex-silver">
        <input type="checkbox" checked={!!v.active} onChange={set("active")} data-testid={`${prefix}-active`} className="accent-[#d4af37]" /> Active
      </label>
      <label className="space-y-1 block col-span-full"><span className="nds-label-dim text-[9px]">Notes</span>
        <input value={v.notes || ""} onChange={set("notes")} data-testid={`${prefix}-notes`} className={`${field} w-full`} /></label>
    </div>
  );
}

function VendorForm({ onDone, onCancel }) {
  const [v, setV] = useState(blankVendor);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setV((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await finApi.post("/vendors", v); toast.success("Vendor added"); onDone(); }
    catch (err) { toast.error(finErr(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="fin-vendor-form" className="p-4 border border-nex-gold/30 rounded-sm space-y-3 bg-white/[0.02]">
      <VendorFields v={v} set={set} prefix="fin-vendor-form" />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} data-testid="fin-vendor-form-cancel" className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Cancel</button>
        <button type="submit" disabled={busy || !v.name.trim()} data-testid="fin-vendor-form-submit" className={btnGold}>Add vendor</button>
      </div>
    </form>
  );
}

export default function VendorsView({ onOpen }) {
  const [adding, setAdding] = useState(false);
  const [state, load] = useLoad(() => finApi.get("/vendors"), []);
  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="px-5 py-3 flex items-center gap-2 border-b border-white/5">
        {!adding && <button type="button" onClick={() => setAdding(true)} data-testid="fin-vendor-new" className={`${btnGold} inline-flex items-center gap-1.5`}><Plus className="h-3 w-3" /> New vendor</button>}
        <button type="button" onClick={load} data-testid="fin-vendor-refresh" aria-label="Refresh" className="ml-auto p-1.5 text-nex-muted hover:text-nex-gold"><RotateCw className="h-3.5 w-3.5" /></button>
      </div>
      {adding && <div className="px-5 pt-3"><VendorForm onCancel={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} /></div>}
      {state.loading && !state.data ? <Loading testid="fin-vendor-loading" label="Loading vendors…" />
        : state.error ? <ErrorBox testid="fin-vendor-error" error={state.error} onRetry={load} />
        : state.data.vendors.length ? (
          <ul data-testid="fin-vendor-list" className="flex-1 overflow-y-auto divide-y divide-white/5">
            {state.data.vendors.map((v) => {
              const [tone, label] = VENDOR_TYPE_CHIP[v.type];
              return (
                <li key={v.id}>
                  <button type="button" onClick={() => onOpen(v.id)} data-testid={`fin-vendor-row-${v.id}`} className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-nex-gold/5 transition-colors">
                    <span className="flex-1 min-w-0 truncate text-nex-silver font-medium">{v.name}</span>
                    <span className="text-xs text-nex-muted truncate max-w-[180px]">{v.contact_name}</span>
                    <Chip tone={tone} data-testid={`fin-vendor-type-${v.id}`}>{label}</Chip>
                    <Chip tone={v.active ? "green" : "grey"} data-testid={`fin-vendor-active-${v.id}`}>{v.active ? "Active" : "Inactive"}</Chip>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : <Empty testid="fin-vendor-empty">No vendors yet.</Empty>}
    </div>
  );
}
